import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Paper, Typography, Card, CardContent, Stack, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, Chip, CircularProgress, FormControl, InputLabel, Select, MenuItem,
  TextField, InputAdornment, Divider, Checkbox, Dialog,
  DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText,
  ListItemSecondaryAction, Grid, IconButton, FormControlLabel, TablePagination,
  Accordion, AccordionSummary, AccordionDetails, Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { 
  IconUsers, IconUserCheck, IconCurrencyDollar, IconAlertCircle,
  IconRefresh, IconSteeringWheel
} from '@tabler/icons-react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PaymentIcon from '@mui/icons-material/Payment';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import PrintIcon from '@mui/icons-material/Print';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import UndoIcon from '@mui/icons-material/Undo';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import {
  fetchOpenItems, checkCanTransition, startTransition,
  fetchYearsWithOpenItems, setSelectedYear, setTransitionData, closeStudentFee, closeAllStudentFees,
  transferUncollectedCheques, transferPendingPayments, completeTransition,
  fetchTransferredStudents, reopenStudentFee,
  fetchPendingTransferCheques, undoTransferCheques,
  fetchPendingTransferPayments, undoTransferPayments
} from '../slices/yearTransitionSlice';
import { updateRecipient, fetchRecipientsByStudent, createRecipient } from '../slices/recipientSlice';
import { fetchSchoolYears } from '../slices/schoolYearsSlice';
import { useToast } from '../context/ToastContext';
import { useNavigate } from 'react-router-dom';
import { paymentTypes } from '../constants/paymentTypes';
import { motion } from 'framer-motion';

const MotionPaper = motion(Paper);

// Design System tokens
const DS_FONT = 'Cairo, Tajawal, sans-serif';
const DS_SHADOW = '0 2px 12px rgba(67,56,202,0.07)';
const DS_BORDER = '1px solid #E5E7EB';

const YearTransitionPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { 
    openItems, loading, canTransition, transitionData, 
    yearsWithOpenItems, selectedYearId 
  } = useSelector(state => state.yearTransition);
  const [localSelectedYear, setLocalSelectedYear] = useState(null);
  const schoolYears = useSelector(state => state.schoolYears?.list || []);
  const activeYearId = schoolYears.find(year => year.is_active)?.id || localSelectedYear || null;

  const uiPreferences = JSON.parse(localStorage.getItem('uiPreferences') || '{}');
  const canStartYearTransition = uiPreferences?.administrative?.year_transition ?? true;
  
  // Payment dialog state
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedStudentForPayment, setSelectedStudentForPayment] = useState(null);
  const [paymentData, setPaymentData] = useState({
    amount: '',
    payment_type: '',
    date: new Date().toISOString().split('T')[0],
    note: '',
    received: false
  });
  const [isSavingPayment, setIsSavingPayment] = useState(false);
  
  // Cheque approval dialog state
  const [chequeApprovalDialog, setChequeApprovalDialog] = useState({
    open: false,
    cheque: null
  });

  // Carry debt forward dialog state
  const [carryDebtDialogOpen, setCarryDebtDialogOpen] = useState(false);
  const [selectedStudentForCarry, setSelectedStudentForCarry] = useState(null);
  const [carryAmount, setCarryAmount] = useState('');
  const [isCarryingDebt, setIsCarryingDebt] = useState(false);

  // Bulk carry-all-debt dialog state
  const [carryAllDialogOpen, setCarryAllDialogOpen] = useState(false);
  const [isCarryingAllDebt, setIsCarryingAllDebt] = useState(false);

  // Bulk transfer-all-cheques dialog state
  const [transferAllChequesDialogOpen, setTransferAllChequesDialogOpen] = useState(false);
  const [isTransferringAllCheques, setIsTransferringAllCheques] = useState(false);

  // Bulk transfer-all-pending-payments dialog state
  const [transferAllPaymentsDialogOpen, setTransferAllPaymentsDialogOpen] = useState(false);
  const [isTransferringAllPayments, setIsTransferringAllPayments] = useState(false);

  const [isCompletingTransition, setIsCompletingTransition] = useState(false);
  const [passToNextYearDialogOpen, setPassToNextYearDialogOpen] = useState(false);
  const [isPreparingPassToNextYear, setIsPreparingPassToNextYear] = useState(false);

  // Table pagination state (client-side)
  const [studentsPage, setStudentsPage] = useState(0);
  const [studentsRowsPerPage, setStudentsRowsPerPage] = useState(10);
  const [chequesPage, setChequesPage] = useState(0);
  const [chequesRowsPerPage, setChequesRowsPerPage] = useState(10);
  const [paymentsPage, setPaymentsPage] = useState(0);
  const [paymentsRowsPerPage, setPaymentsRowsPerPage] = useState(10);
  const [employeesPage, setEmployeesPage] = useState(0);
  const [employeesRowsPerPage, setEmployeesRowsPerPage] = useState(10);

  // When printing, show every row of every table instead of just the current page
  const [isPrinting, setIsPrinting] = useState(false);
  useEffect(() => {
    const handleBeforePrint = () => setIsPrinting(true);
    const handleAfterPrint = () => setIsPrinting(false);
    window.addEventListener('beforeprint', handleBeforePrint);
    window.addEventListener('afterprint', handleAfterPrint);
    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint);
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, []);

  // Transferred (closed) students dialog state
  const [transferredStudentsCount, setTransferredStudentsCount] = useState(0);
  const [transferredStudentsDialogOpen, setTransferredStudentsDialogOpen] = useState(false);
  const [transferredStudentsList, setTransferredStudentsList] = useState([]);
  const [transferredStudentsSearch, setTransferredStudentsSearch] = useState('');
  const [isLoadingTransferredStudents, setIsLoadingTransferredStudents] = useState(false);
  const [reopeningStudentId, setReopeningStudentId] = useState(null);
  const transferredSearchTimeoutRef = useRef(null);

  // Transferred (pending-transfer) cheques dialog state
  const [transferredChequesCount, setTransferredChequesCount] = useState(0);
  const [transferredChequesDialogOpen, setTransferredChequesDialogOpen] = useState(false);
  const [transferredChequesList, setTransferredChequesList] = useState([]);
  const [transferredChequesSearch, setTransferredChequesSearch] = useState('');
  const [isLoadingTransferredCheques, setIsLoadingTransferredCheques] = useState(false);
  const [restoringChequeId, setRestoringChequeId] = useState(null);
  const transferredChequesSearchTimeoutRef = useRef(null);

  // Transferred (pending-transfer) payments dialog state
  const [transferredPaymentsCount, setTransferredPaymentsCount] = useState(0);
  const [transferredPaymentsDialogOpen, setTransferredPaymentsDialogOpen] = useState(false);
  const [transferredPaymentsList, setTransferredPaymentsList] = useState([]);
  const [transferredPaymentsSearch, setTransferredPaymentsSearch] = useState('');
  const [isLoadingTransferredPayments, setIsLoadingTransferredPayments] = useState(false);
  const [restoringPaymentId, setRestoringPaymentId] = useState(null);
  const transferredPaymentsSearchTimeoutRef = useRef(null);

  // Transferred payment detail view (no dedicated single-payment page to open, so show inline)
  const [viewTransferredPayment, setViewTransferredPayment] = useState(null);
  const [viewTransferredStudent, setViewTransferredStudent] = useState(null);
  const [viewTransferredCheque, setViewTransferredCheque] = useState(null);


  useEffect(() => {
    dispatch(checkCanTransition());
    dispatch(fetchYearsWithOpenItems());
    dispatch(fetchSchoolYears());
  }, [dispatch]);
  

  useEffect(() => {
    // Set default to active year - prioritize active year from schoolYears, then from yearsWithOpenItems
    if (!localSelectedYear) {
      // First, try to get active year from schoolYears (same source as Settings page)
      const activeYearFromSchoolYears = schoolYears.find(y => y.is_active);
      if (activeYearFromSchoolYears && yearsWithOpenItems.length > 0) {
        // Find the matching year in yearsWithOpenItems by ID
        const matchingYear = yearsWithOpenItems.find(y => y.id === activeYearFromSchoolYears.id);
        if (matchingYear) {
          setLocalSelectedYear(matchingYear.id);
          dispatch(setSelectedYear(matchingYear.id));
          return;
        }
      }
      
      // Fallback: use active year from yearsWithOpenItems or first year
      if (yearsWithOpenItems.length > 0) {
        const activeYear = yearsWithOpenItems.find(y => y.is_active) || yearsWithOpenItems[0];
        if (activeYear) {
          setLocalSelectedYear(activeYear.id);
          dispatch(setSelectedYear(activeYear.id));
        }
      }
    }
  }, [yearsWithOpenItems, schoolYears, localSelectedYear, dispatch]);

  useEffect(() => {
    if (localSelectedYear) {
      dispatch(fetchOpenItems(localSelectedYear));
      dispatch(fetchTransferredStudents({ yearId: localSelectedYear }))
        .unwrap()
        .then((result) => {
          setTransferredStudentsCount(result.count || 0);
          setTransferredStudentsList(result.items || []);
        })
        .catch(() => { setTransferredStudentsCount(0); setTransferredStudentsList([]); });
      dispatch(fetchPendingTransferCheques({ yearId: localSelectedYear }))
        .unwrap()
        .then((result) => {
          setTransferredChequesCount(result.count || 0);
          setTransferredChequesList(result.items || []);
        })
        .catch(() => { setTransferredChequesCount(0); setTransferredChequesList([]); });
      dispatch(fetchPendingTransferPayments({ yearId: localSelectedYear }))
        .unwrap()
        .then((result) => {
          setTransferredPaymentsCount(result.count || 0);
          setTransferredPaymentsList(result.items || []);
        })
        .catch(() => { setTransferredPaymentsCount(0); setTransferredPaymentsList([]); });
    }
    setStudentsPage(0);
    setChequesPage(0);
    setPaymentsPage(0);
    setEmployeesPage(0);
  }, [localSelectedYear, dispatch]);

  const handleYearChange = (event) => {
    const yearId = event.target.value;
    setLocalSelectedYear(yearId);
    dispatch(setSelectedYear(yearId));
    dispatch(fetchOpenItems(yearId));
  };

  const handleCompleteTransition = async () => {
    if (!transitionData?.transition_id) return;
    setIsCompletingTransition(true);
    try {
      await dispatch(completeTransition(transitionData.transition_id)).unwrap();
      showToast('✅ تم إتمام الانتقال للسنة الدراسية الجديدة بنجاح');
      setPassToNextYearDialogOpen(false);
      dispatch(checkCanTransition());
      dispatch(fetchYearsWithOpenItems());
      dispatch(fetchSchoolYears());
    } catch (error) {
      showToast('❌ فشل في إتمام الانتقال', 'error');
    } finally {
      setIsCompletingTransition(false);
    }
  };

  // Entry point for the full-width "الانتقال إلى السنة الدراسية الجديدة" button.
  // Starts the transition if one hasn't been started yet, loads the itemized lists needed
  // for the summary dialog, then opens it.
  const handleOpenPassToNextYear = async () => {
    setIsPreparingPassToNextYear(true);
    try {
      if (!transitionData) {
        try {
          await dispatch(startTransition()).unwrap();
        } catch (error) {
          if (error?.transition_id) {
            // A transition is already in progress on the backend (e.g. started in an
            // earlier session) but the frontend hadn't picked it up yet — adopt it
            // instead of blocking the user.
            dispatch(setTransitionData({ transition_id: error.transition_id, new_year: error.new_year || null }));
          } else {
            showToast(error?.error || '❌ فشل في بدء عملية الانتقال', 'error');
            return;
          }
        }
      }
      await Promise.all([
        loadTransferredStudents(''),
        loadTransferredCheques(''),
        loadTransferredPayments(''),
      ]);
      setPassToNextYearDialogOpen(true);
    } catch (error) {
      showToast(error?.error || '❌ فشل في تحضير ملخص الانتقال', 'error');
    } finally {
      setIsPreparingPassToNextYear(false);
    }
  };

  const handleRefresh = () => {
    dispatch(fetchYearsWithOpenItems());
    if (localSelectedYear) {
      dispatch(fetchOpenItems(localSelectedYear));
    } else {
      dispatch(fetchOpenItems());
    }
  };

  const handlePrint = () => {
    setIsPrinting(true);
    setTimeout(() => window.print(), 50);
  };

  // Build one combined CSV of all open-item categories for the selected year and trigger a download
  const handleExportCsv = () => {
    if (!openItems) return;

    const escapeCsv = (value) => {
      const str = value === null || value === undefined ? '' : String(value);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    let rows;
    let filenamePrefix;

    if (isClosedYear) {
      const totalDebt = transferredStudentsList.reduce((sum, s) => sum + (s.carry_forward_amount || 0), 0);
      const totalCheques = transferredChequesList.reduce((sum, c) => sum + (c.amount || 0), 0);
      const totalPayments = transferredPaymentsList.reduce((sum, p) => sum + (p.amount || 0), 0);

      rows = [
        [`تقرير السنة المغلقة: ${school_year?.label || ''}`],
        [],
        ['ملخص'],
        ['دين طلاب مرحّل', transferredStudentsList.length, totalDebt.toFixed(2)],
        ['شيكات مرحّلة', transferredChequesList.length, totalCheques.toFixed(2)],
        ['دفعات مرحّلة', transferredPaymentsList.length, totalPayments.toFixed(2)],
        ['الإجمالي', transferredStudentsList.length + transferredChequesList.length + transferredPaymentsList.length, (totalDebt + totalCheques + totalPayments).toFixed(2)],
        [],
        ['النوع', 'الاسم/الرقم', 'التفاصيل', 'المبلغ (₪)'],
      ];
      transferredStudentsList.forEach((s) => {
        rows.push(['طالب مرحّل', s.name, `${s.class || '-'} — ${s.student_number || ''}`, s.carry_forward_amount.toFixed(2)]);
      });
      transferredChequesList.forEach((c) => {
        rows.push(['شيك مرحّل', c.student_name, `رقم الشيك: ${c.cheque_number || '-'}`, c.amount.toFixed(2)]);
      });
      transferredPaymentsList.forEach((p) => {
        rows.push(['دفعة مرحّلة', p.recipient_name || '-', `رقم الشيك: ${p.cheque_number || '-'}`, p.amount.toFixed(2)]);
      });
      filenamePrefix = 'تقرير-سنة-مغلقة';
    } else {
      rows = [['النوع', 'الاسم/الرقم', 'التفاصيل', 'المبلغ (₪)']];

      (students?.items || []).forEach((s) => {
        rows.push(['طالب', s.name, `${s.class || '-'} — ${s.student_id || ''}`, s.outstanding?.toFixed(2) || '0.00']);
      });
      (cheques?.items || []).forEach((c) => {
        rows.push(['شيك غير مستلم', c.student_name, `رقم الشيك: ${c.cheque_number || '-'}`, c.amount?.toFixed(2) || '0.00']);
      });
      (payments?.items || []).forEach((p) => {
        rows.push(['دفعة معلقة', p.recipient_name || '-', `رقم الشيك: ${p.cheque_number || '-'}`, p.amount?.toFixed(2) || '0.00']);
      });
      (employees?.items || []).forEach((e) => {
        rows.push(['راتب غير مُنهى', e.employee_name, e.month, e.outstanding_balance?.toFixed(2) || '0.00']);
      });
      filenamePrefix = 'العناصر-المفتوحة';
    }

    const csvContent = '﻿' + rows.map((row) => row.map(escapeCsv).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filenamePrefix}-${openItems?.school_year?.label || ''}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Handle student payment button click
  const handleStudentPaymentClick = (student) => {
    setSelectedStudentForPayment(student);
    setPaymentData({
      amount: student.outstanding > 0 ? student.outstanding.toFixed(2) : '',
      payment_type: '',
      date: new Date().toISOString().split('T')[0],
      note: '',
      received: false
    });
    setPaymentDialogOpen(true);
  };

  // Handle payment dialog save
  const handlePaymentSave = async () => {
    if (!selectedStudentForPayment || !paymentData.amount || !paymentData.payment_type || !paymentData.date) {
      showToast('يرجى ملء جميع الحقول المطلوبة', 'error');
      return;
    }

    setIsSavingPayment(true);
    try {
      const yearId = localSelectedYear || activeYearId;
      if (!yearId) {
        showToast('يرجى اختيار سنة دراسية', 'error');
        setIsSavingPayment(false);
        return;
      }

      const payload = {
        student_id: selectedStudentForPayment.id,
        amount: Number(paymentData.amount),
        payment_type: paymentData.payment_type,
        date: paymentData.date,
        school_year: yearId,
        received: paymentData.received,
        note: paymentData.note || ''
      };

      await dispatch(createRecipient(payload)).unwrap();
      showToast('تم إضافة الدفعة بنجاح', 'success');
      setPaymentDialogOpen(false);
      setSelectedStudentForPayment(null);
      setPaymentData({
        amount: '',
        payment_type: '',
        date: new Date().toISOString().split('T')[0],
        note: '',
        received: false
      });
      // Refresh open items
      if (localSelectedYear) {
        dispatch(fetchOpenItems(localSelectedYear));
      }
    } catch (error) {
      showToast('فشل في إضافة الدفعة', 'error');
      console.error('Payment error:', error);
    } finally {
      setIsSavingPayment(false);
    }
  };

  // Handle "carry debt forward" button click
  const handleCarryDebtClick = (student) => {
    setSelectedStudentForCarry(student);
    setCarryAmount(student.outstanding > 0 ? student.outstanding.toFixed(2) : '0.00');
    setCarryDebtDialogOpen(true);
  };

  // Handle "carry debt forward" confirmation
  const handleCarryDebtConfirm = async () => {
    if (!selectedStudentForCarry) return;
    const yearId = localSelectedYear || activeYearId;
    if (!yearId) {
      showToast('يرجى اختيار سنة دراسية', 'error');
      return;
    }

    setIsCarryingDebt(true);
    try {
      await dispatch(closeStudentFee({
        studentId: selectedStudentForCarry.id,
        yearId,
        amount: Number(carryAmount),
      })).unwrap();
      showToast('✅ تم ترحيل الدين وإغلاق حساب الطالب لهذه السنة');
      setCarryDebtDialogOpen(false);
      setSelectedStudentForCarry(null);
      dispatch(fetchYearsWithOpenItems());
      dispatch(fetchOpenItems(yearId));
    } catch (error) {
      showToast(error?.error || '❌ فشل ترحيل الدين', 'error');
    } finally {
      setIsCarryingDebt(false);
    }
  };

  // Handle bulk "carry all debt forward" confirmation
  const handleCarryAllDebtConfirm = async () => {
    const yearId = localSelectedYear || activeYearId;
    if (!yearId) {
      showToast('يرجى اختيار سنة دراسية', 'error');
      return;
    }

    setIsCarryingAllDebt(true);
    try {
      const result = await dispatch(closeAllStudentFees({ yearId })).unwrap();
      showToast(`✅ تم ترحيل الدين وإغلاق حساب ${result.closed_count} طالب`);
      setCarryAllDialogOpen(false);
      dispatch(fetchYearsWithOpenItems());
      dispatch(fetchOpenItems(yearId));
    } catch (error) {
      showToast(error?.error || '❌ فشل ترحيل الديون', 'error');
    } finally {
      setIsCarryingAllDebt(false);
    }
  };

  // Handle bulk "transfer all uncollected cheques to next year" confirmation
  const handleTransferAllChequesConfirm = async () => {
    const yearId = localSelectedYear || activeYearId;
    if (!yearId) {
      showToast('يرجى اختيار سنة دراسية', 'error');
      return;
    }

    setIsTransferringAllCheques(true);
    try {
      const result = await dispatch(transferUncollectedCheques({ yearId })).unwrap();
      showToast(`✅ تم ترحيل ${result.transferred_count} شيك للسنة القادمة`);
      setTransferAllChequesDialogOpen(false);
      dispatch(fetchYearsWithOpenItems());
      dispatch(fetchOpenItems(yearId));
    } catch (error) {
      showToast(error?.error || '❌ فشل ترحيل الشيكات', 'error');
    } finally {
      setIsTransferringAllCheques(false);
    }
  };

  // Handle bulk "transfer all pending payment cheques to next year" confirmation
  const handleTransferAllPaymentsConfirm = async () => {
    const yearId = localSelectedYear || activeYearId;
    if (!yearId) {
      showToast('يرجى اختيار سنة دراسية', 'error');
      return;
    }

    setIsTransferringAllPayments(true);
    try {
      const result = await dispatch(transferPendingPayments({ yearId })).unwrap();
      showToast(`✅ تم ترحيل ${result.transferred_count} شيك دفعات للسنة القادمة`);
      setTransferAllPaymentsDialogOpen(false);
      dispatch(fetchYearsWithOpenItems());
      dispatch(fetchOpenItems(yearId));
    } catch (error) {
      showToast(error?.error || '❌ فشل ترحيل شيكات الدفعات', 'error');
    } finally {
      setIsTransferringAllPayments(false);
    }
  };

  // Handle transferring a single uncollected cheque to next year
  const handleTransferSingleCheque = async (cheque) => {
    const yearId = localSelectedYear || activeYearId;
    if (!yearId) {
      showToast('يرجى اختيار سنة دراسية', 'error');
      return;
    }
    try {
      await dispatch(transferUncollectedCheques({ yearId, ids: [cheque.id] })).unwrap();
      showToast('✅ تم ترحيل الشيك للسنة القادمة');
      dispatch(fetchYearsWithOpenItems());
      dispatch(fetchOpenItems(yearId));
    } catch (error) {
      showToast(error?.error || '❌ فشل ترحيل الشيك', 'error');
    }
  };

  // Handle transferring a single pending payment's cheque to next year
  const handleTransferSinglePayment = async (payment) => {
    const yearId = localSelectedYear || activeYearId;
    if (!yearId) {
      showToast('يرجى اختيار سنة دراسية', 'error');
      return;
    }
    try {
      await dispatch(transferPendingPayments({ yearId, ids: [payment.id] })).unwrap();
      showToast('✅ تم ترحيل شيك الدفعة للسنة القادمة');
      dispatch(fetchYearsWithOpenItems());
      dispatch(fetchOpenItems(yearId));
    } catch (error) {
      showToast(error?.error || '❌ فشل ترحيل شيك الدفعة', 'error');
    }
  };

  // Load (or search) the list of students whose fee was closed/transferred for the selected year
  const loadTransferredStudents = async (search) => {
    const yearId = localSelectedYear || activeYearId;
    if (!yearId) return;
    setIsLoadingTransferredStudents(true);
    try {
      const result = await dispatch(fetchTransferredStudents({ yearId, search })).unwrap();
      setTransferredStudentsList(result.items || []);
      setTransferredStudentsCount(result.count || 0);
    } catch (error) {
      showToast(error?.error || '❌ فشل تحميل قائمة الطلاب المرحّلين', 'error');
    } finally {
      setIsLoadingTransferredStudents(false);
    }
  };

  const handleOpenTransferredStudentsDialog = () => {
    setTransferredStudentsDialogOpen(true);
    setTransferredStudentsSearch('');
    loadTransferredStudents('');
  };

  const handleTransferredStudentsSearchChange = (e) => {
    const value = e.target.value;
    setTransferredStudentsSearch(value);
    if (transferredSearchTimeoutRef.current) clearTimeout(transferredSearchTimeoutRef.current);
    transferredSearchTimeoutRef.current = setTimeout(() => loadTransferredStudents(value), 300);
  };

  const handleReopenStudent = async (student) => {
    const yearId = localSelectedYear || activeYearId;
    if (!yearId) return;
    setReopeningStudentId(student.student_id);
    try {
      await dispatch(reopenStudentFee({ studentId: student.student_id, yearId })).unwrap();
      showToast(`✅ تمت إعادة الطالب ${student.name} إلى القائمة الرئيسية`);
      loadTransferredStudents(transferredStudentsSearch);
      dispatch(fetchYearsWithOpenItems());
      dispatch(fetchOpenItems(yearId));
    } catch (error) {
      showToast(error?.error || '❌ فشل التراجع عن الترحيل', 'error');
    } finally {
      setReopeningStudentId(null);
    }
  };

  // Load (or search) the list of cheques queued for transfer to next year
  const loadTransferredCheques = async (search) => {
    const yearId = localSelectedYear || activeYearId;
    if (!yearId) return;
    setIsLoadingTransferredCheques(true);
    try {
      const result = await dispatch(fetchPendingTransferCheques({ yearId, search })).unwrap();
      setTransferredChequesList(result.items || []);
      setTransferredChequesCount(result.count || 0);
    } catch (error) {
      showToast(error?.error || '❌ فشل تحميل قائمة الشيكات المرحّلة', 'error');
    } finally {
      setIsLoadingTransferredCheques(false);
    }
  };

  const handleOpenTransferredChequesDialog = () => {
    setTransferredChequesDialogOpen(true);
    setTransferredChequesSearch('');
    loadTransferredCheques('');
  };

  const handleTransferredChequesSearchChange = (e) => {
    const value = e.target.value;
    setTransferredChequesSearch(value);
    if (transferredChequesSearchTimeoutRef.current) clearTimeout(transferredChequesSearchTimeoutRef.current);
    transferredChequesSearchTimeoutRef.current = setTimeout(() => loadTransferredCheques(value), 300);
  };

  const handleUndoTransferCheque = async (cheque) => {
    setRestoringChequeId(cheque.id);
    try {
      await dispatch(undoTransferCheques({ ids: [cheque.id] })).unwrap();
      showToast('✅ تمت استعادة الشيك إلى القائمة الرئيسية');
      loadTransferredCheques(transferredChequesSearch);
      dispatch(fetchYearsWithOpenItems());
      if (localSelectedYear) dispatch(fetchOpenItems(localSelectedYear));
    } catch (error) {
      showToast(error?.error || '❌ فشل التراجع عن ترحيل الشيك', 'error');
    } finally {
      setRestoringChequeId(null);
    }
  };

  // Load (or search) the list of pending payments queued for transfer to next year
  const loadTransferredPayments = async (search) => {
    const yearId = localSelectedYear || activeYearId;
    if (!yearId) return;
    setIsLoadingTransferredPayments(true);
    try {
      const result = await dispatch(fetchPendingTransferPayments({ yearId, search })).unwrap();
      setTransferredPaymentsList(result.items || []);
      setTransferredPaymentsCount(result.count || 0);
    } catch (error) {
      showToast(error?.error || '❌ فشل تحميل قائمة الدفعات المرحّلة', 'error');
    } finally {
      setIsLoadingTransferredPayments(false);
    }
  };

  const handleOpenTransferredPaymentsDialog = () => {
    setTransferredPaymentsDialogOpen(true);
    setTransferredPaymentsSearch('');
    loadTransferredPayments('');
  };

  const handleTransferredPaymentsSearchChange = (e) => {
    const value = e.target.value;
    setTransferredPaymentsSearch(value);
    if (transferredPaymentsSearchTimeoutRef.current) clearTimeout(transferredPaymentsSearchTimeoutRef.current);
    transferredPaymentsSearchTimeoutRef.current = setTimeout(() => loadTransferredPayments(value), 300);
  };

  const handleUndoTransferPayment = async (payment) => {
    setRestoringPaymentId(payment.id);
    try {
      await dispatch(undoTransferPayments({ ids: [payment.id] })).unwrap();
      showToast('✅ تمت استعادة شيك الدفعة إلى القائمة الرئيسية');
      loadTransferredPayments(transferredPaymentsSearch);
      dispatch(fetchYearsWithOpenItems());
      if (localSelectedYear) dispatch(fetchOpenItems(localSelectedYear));
    } catch (error) {
      showToast(error?.error || '❌ فشل التراجع عن ترحيل شيك الدفعة', 'error');
    } finally {
      setRestoringPaymentId(null);
    }
  };

  // Handle cheque approval button click
  const handleChequeApprovalClick = (cheque) => {
    setChequeApprovalDialog({
      open: true,
      cheque: cheque
    });
  };

  // Handle cheque approval confirmation
  const handleChequeApprovalConfirm = async () => {
    if (!chequeApprovalDialog.cheque) return;

    try {
      // Fetch the recipient to get all its data
      const recipients = await dispatch(fetchRecipientsByStudent(chequeApprovalDialog.cheque.student_id)).unwrap();
      const recipientData = Array.isArray(recipients) 
        ? recipients.find(r => r.id === chequeApprovalDialog.cheque.id) 
        : null;
      
      if (!recipientData) {
        showToast('لم يتم العثور على بيانات الدفعة', 'error');
        return;
      }

      // Update recipient to mark as received
      const payload = {
        id: chequeApprovalDialog.cheque.id,
        student_id: recipientData.student || recipientData.student_id || chequeApprovalDialog.cheque.student_id,
        amount: Number(recipientData.amount || chequeApprovalDialog.cheque.amount),
        date: recipientData.date || chequeApprovalDialog.cheque.payment_date || new Date().toISOString().split('T')[0],
        payment_type: recipientData.payment_type || '',
        received: true,
        note: recipientData.note || chequeApprovalDialog.cheque.note || '',
        ...(recipientData.school_fee && { school_fee: recipientData.school_fee }),
        ...(recipientData.cheque && {
          bankNumber: recipientData.cheque.bank_number,
          branchNumber: recipientData.cheque.branch_number,
          accountNumber: recipientData.cheque.account_number,
          chequeNumber: recipientData.cheque.cheque_number,
          chequeDueDate: recipientData.cheque.cheque_date,
          description: recipientData.cheque.description
        })
      };

      await dispatch(updateRecipient(payload)).unwrap();
      showToast('تم تأكيد تحصيل الشيك بنجاح', 'success');
      setChequeApprovalDialog({ open: false, cheque: null });
      
      // Refresh open items
      if (localSelectedYear) {
        dispatch(fetchOpenItems(localSelectedYear));
      }
    } catch (error) {
      showToast('فشل في تأكيد تحصيل الشيك', 'error');
      console.error('Cheque approval error:', error);
    }
  };

  if (loading && !openItems && yearsWithOpenItems.length === 0) {
    return (
      <Box sx={{ direction: 'rtl', fontFamily: DS_FONT, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#F8F9FF' }}>
        <CircularProgress sx={{ color: '#4338CA' }} />
      </Box>
    );
  }

  if (yearsWithOpenItems.length === 0 && !loading) {
    return (
      <Box sx={{ direction: 'rtl', fontFamily: DS_FONT, p: { xs: 2, md: 3 }, minHeight: '100vh', bgcolor: '#F8F9FF' }}>
        <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: '#fff', boxShadow: DS_SHADOW, border: DS_BORDER }}>
            <Alert severity="success" sx={{ borderRadius: 2, fontFamily: DS_FONT }}>
              <Typography variant="h6" gutterBottom fontWeight={800} sx={{ fontFamily: DS_FONT, color: '#1E1B4B' }}>
                لا توجد عناصر مفتوحة
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: DS_FONT, color: '#6B7280' }}>
                جميع السنوات الدراسية مغلقة ولا توجد مدفوعات معلقة
              </Typography>
            </Alert>
          </Paper>
        </Box>
      </Box>
    );
  }

  if (!openItems) {
    return (
      <Box sx={{ direction: 'rtl', fontFamily: DS_FONT, p: { xs: 2, md: 3 }, minHeight: '100vh', bgcolor: '#F8F9FF' }}>
        <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: '#fff', boxShadow: DS_SHADOW, border: DS_BORDER }}>
            <Alert severity="info" sx={{ borderRadius: 2, fontFamily: DS_FONT }}>
              يرجى اختيار سنة دراسية لعرض العناصر المفتوحة
            </Alert>
          </Paper>
        </Box>
      </Box>
    );
  }

  const { students, employees, cheques, payments, summary, school_year } = openItems || {};
  
  // Verify that the displayed data matches the selected year
  const isDataForSelectedYear = school_year && localSelectedYear && school_year.id === localSelectedYear;

  // A closed (inactive) year is a locked historical reference - no actions allowed, view-only
  const isClosedYear = !!(school_year && school_year.is_active === false);

  return (
    // 🎨 Design System Applied
    <Box
      id="year-transition-printable"
      sx={{
        direction: 'rtl',
        fontFamily: DS_FONT,
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
    >
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #year-transition-printable, #year-transition-printable * { visibility: visible; }
          #year-transition-printable { position: absolute; left: 0; top: 0; width: 100%; padding: 0; }
          .no-print, .no-print * { display: none !important; }
        }
      `}</style>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      {/* Header Section */}
      <MotionPaper
        component={motion.div}
        elevation={0}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
          color: '#fff',
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 2.5,
                width: 52,
                height: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <IconAlertCircle size={30} color="#fff" />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={800} color="#fff" sx={{ fontFamily: DS_FONT }}>
                العناصر المفتوحة للسنة الدراسية
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 0.25, fontFamily: DS_FONT }}>
                متابعة الحسابات المفتوحة وإتمام انتقال السنة الدراسية
              </Typography>
            </Box>
          </Stack>
          <Stack className="no-print" useFlexGap direction="row" spacing={1.5} flexWrap="wrap" gap={1.5}>
            <Button
              className="no-print"
              variant="outlined"
              startIcon={<IconRefresh />}
              onClick={handleRefresh}
              disabled={loading}
              sx={{
                px: 2.5,
                py: 1.1,
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: DS_FONT,
                color: '#fff',
                borderColor: 'rgba(255,255,255,0.6)',
                '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.12)' },
              }}
            >
              تحديث
            </Button>
            <Button
              className="no-print"
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              disabled={!openItems}
              sx={{
                px: 2.5,
                py: 1.1,
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: DS_FONT,
                color: '#fff',
                borderColor: 'rgba(255,255,255,0.6)',
                '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.12)' },
              }}
            >
              طباعة
            </Button>
            <Button
              className="no-print"
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportCsv}
              disabled={!openItems}
              sx={{
                px: 2.5,
                py: 1.1,
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: DS_FONT,
                color: '#fff',
                borderColor: 'rgba(255,255,255,0.6)',
                '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.12)' },
              }}
            >
              تصدير Excel
            </Button>
          </Stack>
        </Stack>
      </MotionPaper>

      {/* Pass to Next Year — full-width entry point (only relevant while viewing the active year) */}
      {!isClosedYear && (
      <Tooltip
        title={canStartYearTransition ? '' : 'هذه الميزة غير مفعّلة لحسابكم، يرجى التواصل معنا لتفعيلها'}
        arrow
      >
        <span style={{ display: 'block' }}>
          <Button
            className="no-print"
            component={motion.button}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.05 }}
            fullWidth
            variant="contained"
            onClick={handleOpenPassToNextYear}
            disabled={loading || isPreparingPassToNextYear || !canStartYearTransition}
            startIcon={!isPreparingPassToNextYear && <TrendingFlatIcon />}
            sx={{
              mb: 3,
              py: 1.75,
              borderRadius: 3,
              bgcolor: transitionData ? '#16A34A' : '#4338CA',
              color: '#fff',
              fontWeight: 800,
              fontSize: '1.05rem',
              textTransform: 'none',
              fontFamily: DS_FONT,
              boxShadow: DS_SHADOW,
              '&:hover': { bgcolor: transitionData ? '#15803D' : '#3730A3', boxShadow: DS_SHADOW },
            }}
          >
            {isPreparingPassToNextYear
              ? 'جاري التحضير...'
              : transitionData
                ? 'مراجعة وإتمام الانتقال إلى السنة الدراسية الجديدة'
                : 'الانتقال إلى السنة الدراسية الجديدة'}
          </Button>
        </span>
      </Tooltip>
      )}

      {/* Year Selector Card */}
      <MotionPaper
        component={motion.div}
        elevation={0}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          borderRadius: 3,
          bgcolor: '#fff',
          boxShadow: DS_SHADOW,
          border: DS_BORDER,
        }}
      >
        {/* Year Selector */}
        {yearsWithOpenItems.length > 0 && (
          <Box className="no-print" sx={{ mb: 3 }}>
            <FormControl fullWidth>
              <InputLabel>اختر السنة الدراسية</InputLabel>
              <Select
                value={localSelectedYear || ''}
                onChange={handleYearChange}
                label="اختر السنة الدراسية"
                renderValue={(value) => {
                  const selectedYear = yearsWithOpenItems.find(y => y.id === value);
                  if (!selectedYear) return '';
                  return (
                    <Stack useFlexGap 
                      direction="row" 
                      alignItems="center" 
                      justifyContent="space-between"
                      spacing={1} 
                      sx={{ width: '100%', py: 0.5 }}
                    >
                      <Typography sx={{ flex: 1, textAlign: 'right' }}>{selectedYear.label}</Typography>
                      <Stack useFlexGap direction="row" spacing={0.5} alignItems="center">
                        {selectedYear.is_active && (
                          <Chip 
                            label="نشطة" 
                            color="success" 
                            size="small" 
                            sx={{ height: '24px', fontSize: '0.75rem' }} 
                          />
                        )}
                        {selectedYear.has_open_items && (
                          <Chip 
                            label="عناصر مفتوحة" 
                            color="error" 
                            size="small" 
                            sx={{ height: '24px', fontSize: '0.75rem' }} 
                          />
                        )}
                      </Stack>
                    </Stack>
                  );
                }}
              >
                {[...yearsWithOpenItems]
                  .sort((a, b) => {
                    // Sort: active year first, then by label (newest first)
                    if (a.is_active && !b.is_active) return -1;
                    if (!a.is_active && b.is_active) return 1;
                    return b.label.localeCompare(a.label);
                  })
                  .map((year) => (
                    <MenuItem key={year.id} value={year.id}>
                      <Stack useFlexGap 
                        direction="row" 
                        alignItems="center" 
                        justifyContent="space-between"
                        spacing={1} 
                        sx={{ width: '100%', minHeight: '40px' }}
                      >
                        <Typography sx={{ flex: 1, textAlign: 'right' }}>{year.label}</Typography>
                        <Stack useFlexGap direction="row" spacing={0.5} alignItems="center">
                          {year.is_active && (
                            <Chip 
                              label="نشطة" 
                              color="success" 
                              size="small" 
                              sx={{ height: '24px', fontSize: '0.75rem' }} 
                            />
                          )}
                          {year.has_open_items && (
                            <Chip 
                              label="عناصر مفتوحة" 
                              color="error" 
                              size="small" 
                              sx={{ height: '24px', fontSize: '0.75rem' }} 
                            />
                          )}
                        </Stack>
                      </Stack>
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {openItems?.school_year && (
          <Alert severity={isClosedYear ? 'default' : 'info'} sx={{ mb: 0, borderRadius: 2, fontFamily: DS_FONT, ...(isClosedYear ? { bgcolor: '#F3F4F6', color: '#374151' } : {}) }}>
            <Typography variant="body2" sx={{ fontFamily: DS_FONT }}>
              <strong>السنة الدراسية المحددة:</strong> {openItems.school_year.label}
              {openItems.school_year.is_active ? (
                <Chip label="نشطة" color="success" size="small" sx={{ ml: 1 }} />
              ) : (
                <Chip label="🔒 مغلقة" color="default" size="small" sx={{ ml: 1, fontWeight: 700 }} />
              )}
            </Typography>
            {isClosedYear && (
              <Typography variant="body2" sx={{ fontFamily: DS_FONT, mt: 0.5 }}>
                هذه السنة مغلقة — كل ما يظهر أدناه للمرجعية فقط ولا يمكن التعديل أو التراجع عن أي ترحيل من هنا.
              </Typography>
            )}
          </Alert>
        )}
      </MotionPaper>

      {/* Show warning if data doesn't match selected year */}
      {openItems && !isDataForSelectedYear && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2, fontFamily: DS_FONT }}>
          <Typography variant="body2">
            تحذير: البيانات المعروضة لا تطابق السنة المحددة. جاري التحديث...
          </Typography>
        </Alert>
      )}

      {/* Only show data if it matches the selected year */}
      {isDataForSelectedYear && (
        <>
      {/* Financial Section */}
        {/* Summary Cards — not relevant for a closed year (replaced by the closed-year report below) */}
        {!isClosedYear && (
        <Stack
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
          direction="row"
          spacing={2}
          mb={3}
          flexWrap="wrap"
          useFlexGap
        >
        <Card elevation={0} sx={{ flex: 1, minWidth: 200, borderRadius: 3, bgcolor: '#fff', boxShadow: DS_SHADOW, border: DS_BORDER }}>
          <CardContent>
            <Stack useFlexGap direction="row" alignItems="center" spacing={1} mb={1}>
              <IconUsers size={24} color="#4338CA" />
              <Typography variant="h6" fontWeight={700} sx={{ fontFamily: DS_FONT, color: '#1E1B4B' }}>الطلاب</Typography>
            </Stack>
            <Typography variant="h4" fontWeight={800} sx={{ fontFamily: DS_FONT, color: '#4338CA' }}>{students?.count || 0}</Typography>
            <Typography variant="body2" sx={{ fontFamily: DS_FONT, color: '#6B7280' }}>طالب بحساب مفتوح</Typography>
            <Typography variant="h6" color="error" mt={1} fontWeight={700} sx={{ fontFamily: DS_FONT }}>
              {students?.total_outstanding?.toFixed(2) || 0} ₪
            </Typography>
          </CardContent>
        </Card>

        <Card elevation={0} sx={{ flex: 1, minWidth: 200, borderRadius: 3, bgcolor: '#fff', boxShadow: DS_SHADOW, border: DS_BORDER }}>
          <CardContent>
            <Stack useFlexGap direction="row" alignItems="center" spacing={1} mb={1}>
              <IconCurrencyDollar size={24} color="#00BFFF" />
              <Typography variant="h6" fontWeight={700} sx={{ fontFamily: DS_FONT, color: '#1E1B4B' }}>الشيكات</Typography>
            </Stack>
            <Typography variant="h4" fontWeight={800} sx={{ fontFamily: DS_FONT, color: '#00BFFF' }}>{cheques?.count || 0}</Typography>
            <Typography variant="body2" sx={{ fontFamily: DS_FONT, color: '#6B7280' }}>شيك غير مستلم</Typography>
            <Typography variant="h6" color="error" mt={1} fontWeight={700} sx={{ fontFamily: DS_FONT }}>
              {cheques?.total_amount?.toFixed(2) || 0} ₪
            </Typography>
          </CardContent>
        </Card>

        <Card elevation={0} sx={{ flex: 1, minWidth: 200, borderRadius: 3, bgcolor: '#fff', boxShadow: DS_SHADOW, border: DS_BORDER }}>
          <CardContent>
            <Stack useFlexGap direction="row" alignItems="center" spacing={1} mb={1}>
              <IconUserCheck size={24} color="#0284C7" />
              <Typography variant="h6" fontWeight={700} sx={{ fontFamily: DS_FONT, color: '#1E1B4B' }}>الموظفين</Typography>
            </Stack>
            <Typography variant="h4" fontWeight={800} sx={{ fontFamily: DS_FONT, color: '#0284C7' }}>{employees?.count || 0}</Typography>
            <Typography variant="body2" sx={{ fontFamily: DS_FONT, color: '#6B7280' }}>راتب غير مُنهى</Typography>
            <Typography variant="h6" color="error" mt={1} fontWeight={700} sx={{ fontFamily: DS_FONT }}>
              {employees?.total_outstanding?.toFixed(2) || 0} ₪
            </Typography>
          </CardContent>
        </Card>

        <Card elevation={0} sx={{ flex: 1, minWidth: 200, borderRadius: 3, bgcolor: '#fff', boxShadow: DS_SHADOW, border: DS_BORDER }}>
          <CardContent>
            <Stack useFlexGap direction="row" alignItems="center" spacing={1} mb={1}>
              <IconSteeringWheel size={24} color="#5B21F4" />
              <Typography variant="h6" fontWeight={700} sx={{ fontFamily: DS_FONT, color: '#1E1B4B' }}>المدفوعات</Typography>
            </Stack>
            <Typography variant="h4" fontWeight={800} sx={{ fontFamily: DS_FONT, color: '#5B21F4' }}>{payments?.count || 0}</Typography>
            <Typography variant="body2" sx={{ fontFamily: DS_FONT, color: '#6B7280' }}>دفعة معلقة</Typography>
            <Typography variant="h6" color="error" mt={1} fontWeight={700} sx={{ fontFamily: DS_FONT }}>
              {payments?.total_amount?.toFixed(2) || 0} ₪
            </Typography>
          </CardContent>
        </Card>

        <Card elevation={0} sx={{ flex: 1, minWidth: 200, borderRadius: 3, bgcolor: '#fff', boxShadow: DS_SHADOW, border: `1px solid ${summary?.can_close_year ? '#BBF7D0' : '#FECACA'}` }}>
          <CardContent>
            <Stack useFlexGap direction="row" alignItems="center" spacing={1} mb={1}>
              <IconCurrencyDollar size={24} color={summary?.can_close_year ? "#16A34A" : "#DC2626"} />
              <Typography variant="h6" fontWeight={700} sx={{ fontFamily: DS_FONT, color: '#1E1B4B' }}>الإجمالي</Typography>
            </Stack>
            <Typography variant="h4" fontWeight={800} color={summary?.can_close_year ? "success.main" : "error.main"} sx={{ fontFamily: DS_FONT }}>
              {summary?.total_open_items || 0}
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: DS_FONT, color: '#6B7280' }}>عنصر مفتوح</Typography>
            <Typography variant="h6" color="error" mt={1} fontWeight={700} sx={{ fontFamily: DS_FONT }}>
              {summary?.total_outstanding?.toFixed(2) || 0} ₪
            </Typography>
            <Chip
              label={summary?.can_close_year ? "يمكن إغلاق السنة" : "لا يمكن إغلاق السنة"}
              color={summary?.can_close_year ? "success" : "error"}
              size="small"
              sx={{ mt: 1, fontWeight: 700, fontFamily: DS_FONT }}
            />
          </CardContent>
        </Card>
      </Stack>
        )}

        {/* Closed-year report: full transferred data with names, amounts and totals — printable/exportable */}
        {isClosedYear && (
          <Paper elevation={0} sx={{ mb: 3, borderRadius: 3, bgcolor: '#fff', boxShadow: DS_SHADOW, border: DS_BORDER, overflow: 'hidden' }}>
            <Box sx={{ p: 2, borderBottom: DS_BORDER, bgcolor: '#F3F4F6' }}>
              <Typography variant="h6" fontWeight={800} sx={{ fontFamily: DS_FONT, color: '#1E1B4B' }}>
                🔒 تقرير السنة المغلقة: {school_year?.label}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: DS_FONT, color: '#6B7280', mt: 0.5 }}>
                جميع العناصر المرحّلة للسنة القادمة — للمرجعية فقط
              </Typography>
            </Box>

            {/* Stat cards */}
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ p: 2 }}>
              <Card elevation={0} sx={{ flex: 1, minWidth: 180, borderRadius: 2, bgcolor: '#FFFBEB', border: '1px solid #FDE68A' }}>
                <CardContent>
                  <Typography variant="body2" sx={{ fontFamily: DS_FONT, color: '#92400E' }}>دين طلاب مرحّل ({transferredStudentsList.length})</Typography>
                  <Typography variant="h6" fontWeight={800} sx={{ fontFamily: DS_FONT, color: '#92400E' }}>
                    {transferredStudentsList.reduce((sum, s) => sum + (s.carry_forward_amount || 0), 0).toFixed(2)} ₪
                  </Typography>
                </CardContent>
              </Card>
              <Card elevation={0} sx={{ flex: 1, minWidth: 180, borderRadius: 2, bgcolor: '#FFFBEB', border: '1px solid #FDE68A' }}>
                <CardContent>
                  <Typography variant="body2" sx={{ fontFamily: DS_FONT, color: '#92400E' }}>شيكات مرحّلة ({transferredChequesList.length})</Typography>
                  <Typography variant="h6" fontWeight={800} sx={{ fontFamily: DS_FONT, color: '#92400E' }}>
                    {transferredChequesList.reduce((sum, c) => sum + (c.amount || 0), 0).toFixed(2)} ₪
                  </Typography>
                </CardContent>
              </Card>
              <Card elevation={0} sx={{ flex: 1, minWidth: 180, borderRadius: 2, bgcolor: '#FFFBEB', border: '1px solid #FDE68A' }}>
                <CardContent>
                  <Typography variant="body2" sx={{ fontFamily: DS_FONT, color: '#92400E' }}>دفعات مرحّلة ({transferredPaymentsList.length})</Typography>
                  <Typography variant="h6" fontWeight={800} sx={{ fontFamily: DS_FONT, color: '#92400E' }}>
                    {transferredPaymentsList.reduce((sum, p) => sum + (p.amount || 0), 0).toFixed(2)} ₪
                  </Typography>
                </CardContent>
              </Card>
              <Card elevation={0} sx={{ flex: 1, minWidth: 180, borderRadius: 2, bgcolor: '#EEF2FF', border: '1px solid #C7D2FE' }}>
                <CardContent>
                  <Typography variant="body2" sx={{ fontFamily: DS_FONT, color: '#3730A3' }}>
                    الإجمالي ({transferredStudentsList.length + transferredChequesList.length + transferredPaymentsList.length})
                  </Typography>
                  <Typography variant="h6" fontWeight={800} sx={{ fontFamily: DS_FONT, color: '#3730A3' }}>
                    {(
                      transferredStudentsList.reduce((sum, s) => sum + (s.carry_forward_amount || 0), 0) +
                      transferredChequesList.reduce((sum, c) => sum + (c.amount || 0), 0) +
                      transferredPaymentsList.reduce((sum, p) => sum + (p.amount || 0), 0)
                    ).toFixed(2)} ₪
                  </Typography>
                </CardContent>
              </Card>
            </Stack>

            {/* Transferred students table */}
            {transferredStudentsList.length > 0 && (
              <Box sx={{ px: 2, pb: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ fontFamily: DS_FONT, mb: 1 }}>الطلاب المرحّلون</Typography>
                <TableContainer sx={{ border: DS_BORDER, borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>اسم الطالب</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>رقم الطالب</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>الصف</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>المدفوع</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>الدين المرحّل</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>تاريخ الإغلاق</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transferredStudentsList.map((s) => (
                        <TableRow key={s.history_id}>
                          <TableCell>{s.name}</TableCell>
                          <TableCell>{s.student_number || '-'}</TableCell>
                          <TableCell>{s.class || '-'}</TableCell>
                          <TableCell align="right">{s.total_paid.toFixed(2)} ₪</TableCell>
                          <TableCell align="right">{s.carry_forward_amount.toFixed(2)} ₪</TableCell>
                          <TableCell>{s.closed_at ? new Date(s.closed_at).toLocaleDateString('en-GB') : '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Transferred cheques table */}
            {transferredChequesList.length > 0 && (
              <Box sx={{ px: 2, pb: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ fontFamily: DS_FONT, mb: 1 }}>الشيكات المرحّلة</Typography>
                <TableContainer sx={{ border: DS_BORDER, borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>اسم الطالب</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>المبلغ</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>رقم الشيك</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>تاريخ الدفع</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>الحالة</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transferredChequesList.map((c) => (
                        <TableRow key={c.id}>
                          <TableCell>{c.student_name}</TableCell>
                          <TableCell align="right">{c.amount.toFixed(2)} ₪</TableCell>
                          <TableCell>{c.cheque_number || '-'}</TableCell>
                          <TableCell>{c.payment_date ? new Date(c.payment_date).toLocaleDateString('en-GB') : '-'}</TableCell>
                          <TableCell>{c.moved ? 'انتقلت للسنة الجديدة' : 'لا تزال في هذه السنة'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Transferred payments table */}
            {transferredPaymentsList.length > 0 && (
              <Box sx={{ px: 2, pb: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ fontFamily: DS_FONT, mb: 1 }}>الدفعات المرحّلة</Typography>
                <TableContainer sx={{ border: DS_BORDER, borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>المستلم</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>المبلغ</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>رقم الشيك</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>السبب</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>الحالة</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transferredPaymentsList.map((p) => (
                        <TableRow key={p.id}>
                          <TableCell>{p.recipient_name || '-'}</TableCell>
                          <TableCell align="right">{p.amount.toFixed(2)} ₪</TableCell>
                          <TableCell>{p.cheque_number || '-'}</TableCell>
                          <TableCell>{p.reason || '-'}</TableCell>
                          <TableCell>{p.moved ? 'انتقلت للسنة الجديدة' : 'لا تزال في هذه السنة'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {(transferredStudentsList.length + transferredChequesList.length + transferredPaymentsList.length) === 0 && (
              <Box sx={{ p: 3 }}>
                <Alert severity="info">لا توجد عناصر مرحّلة لهذه السنة</Alert>
              </Box>
            )}
          </Paper>
        )}

        {/* Transferred items bar — always visible regardless of remaining open-item counts,
            since a fully-transferred category disappears from the tables below */}
        {!isClosedYear && (transferredStudentsCount + transferredChequesCount + transferredPaymentsCount) > 0 && (
          <Paper
            className="no-print"
            elevation={0}
            sx={{ mb: 3, p: 2, borderRadius: 3, bgcolor: '#FFFBEB', boxShadow: DS_SHADOW, border: '1px solid #FDE68A' }}
          >
            <Stack useFlexGap direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
              <Typography variant="body2" fontWeight={700} sx={{ fontFamily: DS_FONT, color: '#92400E' }}>
                عناصر مرحّلة للسنة القادمة (لم تُحل فعلياً):
              </Typography>
              <Stack useFlexGap direction="row" spacing={1} flexWrap="wrap" gap={1}>
                {transferredStudentsCount > 0 && (
                  <Button
                    variant="outlined"
                    size="small"
                    color="warning"
                    onClick={handleOpenTransferredStudentsDialog}
                    sx={{ borderRadius: 2.5, fontWeight: 700, textTransform: 'none', fontFamily: DS_FONT, bgcolor: '#fff' }}
                  >
                    الطلاب المرحّلون ({transferredStudentsCount})
                  </Button>
                )}
                {transferredChequesCount > 0 && (
                  <Button
                    variant="outlined"
                    size="small"
                    color="warning"
                    onClick={handleOpenTransferredChequesDialog}
                    sx={{ borderRadius: 2.5, fontWeight: 700, textTransform: 'none', fontFamily: DS_FONT, bgcolor: '#fff' }}
                  >
                    الشيكات المرحّلة ({transferredChequesCount})
                  </Button>
                )}
                {transferredPaymentsCount > 0 && (
                  <Button
                    variant="outlined"
                    size="small"
                    color="warning"
                    onClick={handleOpenTransferredPaymentsDialog}
                    sx={{ borderRadius: 2.5, fontWeight: 700, textTransform: 'none', fontFamily: DS_FONT, bgcolor: '#fff' }}
                  >
                    الدفعات المرحّلة ({transferredPaymentsCount})
                  </Button>
                )}
              </Stack>
            </Stack>
          </Paper>
        )}

        {/* Students with Incomplete Fee Payments Table */}
        {students?.count > 0 ? (
          <Paper
            className={isClosedYear ? 'no-print' : ''}
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
            elevation={0}
            sx={{ mb: 3, borderRadius: 3, bgcolor: '#fff', boxShadow: DS_SHADOW, border: DS_BORDER, overflow: 'hidden' }}
          >
            <Box sx={{ p: 2, borderBottom: DS_BORDER, bgcolor: '#EEF2FF' }}>
              <Stack useFlexGap direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                <Stack useFlexGap direction="row" alignItems="center" spacing={1}>
                  <IconUsers size={24} color="#4338CA" />
                  <Typography variant="h6" fontWeight={700} sx={{ fontFamily: DS_FONT, color: '#1E1B4B' }}>الطلاب الذين لم يكملوا دفع الرسوم المدرسية ({students.count})</Typography>
                </Stack>
                <Stack useFlexGap direction="row" spacing={1}>
                  <Button
                    className="no-print"
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={handleOpenTransferredStudentsDialog}
                    sx={{
                      borderRadius: 2.5,
                      fontWeight: 700,
                      textTransform: 'none',
                      fontFamily: DS_FONT,
                    }}
                  >
                    الطلاب المرحّلون ({transferredStudentsCount})
                  </Button>
                  {!isClosedYear && (
                    <Button
                      variant="contained"
                      color="warning"
                      size="small"
                      startIcon={<TrendingFlatIcon />}
                      onClick={() => setCarryAllDialogOpen(true)}
                      sx={{
                        borderRadius: 2.5,
                        fontWeight: 700,
                        textTransform: 'none',
                        fontFamily: DS_FONT,
                        boxShadow: 'none',
                      }}
                    >
                      ترحيل دين جميع الطلاب ({students.count})
                    </Button>
                  )}
                </Stack>
              </Stack>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>اسم الطالب</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>رقم الطالب</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>الصف</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>إجمالي الرسوم</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>المدفوع</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>المتبقي</TableCell>
                    <TableCell className="no-print" align="center" sx={{ fontWeight: 'bold' }}>إجراءات</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(isPrinting ? students.items : students.items.slice(studentsPage * studentsRowsPerPage, studentsPage * studentsRowsPerPage + studentsRowsPerPage)).map((student) => (
                    <TableRow key={student.id} hover>
                      <TableCell>{student.name}</TableCell>
                      <TableCell>{student.student_id || '-'}</TableCell>
                      <TableCell>{student.class || '-'}</TableCell>
                      <TableCell align="right">
                        {student.total_fee?.toFixed(2) || '0.00'} ₪
                        {student.previous_debt > 0 && (
                          <Chip
                            label={`دين سابق: ${student.previous_debt.toFixed(2)} ₪`}
                            color="warning"
                            size="small"
                            sx={{ ml: 1, fontWeight: 700, fontFamily: DS_FONT }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">{student.total_paid?.toFixed(2) || '0.00'} ₪</TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          color={student.outstanding > 0 ? "error.main" : "success.main"}
                          fontWeight="bold"
                        >
                          {student.outstanding?.toFixed(2) || '0.00'} ₪
                        </Typography>
                      </TableCell>
                      <TableCell className="no-print" align="center">
                        {isClosedYear ? (
                          <Typography variant="caption" color="text.secondary">السنة مغلقة</Typography>
                        ) : (
                          <>
                            <IconButton
                              color="primary"
                              onClick={() => handleStudentPaymentClick(student)}
                              size="small"
                              title="إكمال الدفع"
                            >
                              <PaymentIcon />
                            </IconButton>
                            <IconButton
                              color="warning"
                              onClick={() => handleCarryDebtClick(student)}
                              size="small"
                              title="ترحيل الدين للسنة القادمة"
                            >
                              <TrendingFlatIcon />
                            </IconButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              className="no-print"
              component="div"
              count={students.items.length}
              page={studentsPage}
              onPageChange={(e, newPage) => setStudentsPage(newPage)}
              rowsPerPage={studentsRowsPerPage}
              onRowsPerPageChange={(e) => { setStudentsRowsPerPage(parseInt(e.target.value, 10)); setStudentsPage(0); }}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="صفوف لكل صفحة"
              sx={{ direction: 'ltr' }}
            />
          </Paper>
        ) : (
          !isClosedYear && (
            <Paper elevation={0} sx={{ mb: 3, p: 3, borderRadius: 3, bgcolor: '#fff', boxShadow: DS_SHADOW, border: DS_BORDER }}>
              <Alert severity="success" sx={{ borderRadius: 2, fontFamily: DS_FONT }}>
                <Typography variant="body1" sx={{ fontFamily: DS_FONT }}>جميع الطلاب أكملوا دفع الرسوم المدرسية</Typography>
              </Alert>
            </Paper>
          )
        )}

        {/* Uncollected Cheques Table */}
        {cheques?.count > 0 && (
          <Paper
            className={isClosedYear ? 'no-print' : ''}
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
            elevation={0}
            sx={{ mb: 3, borderRadius: 3, bgcolor: '#fff', boxShadow: DS_SHADOW, border: DS_BORDER, overflow: 'hidden' }}
          >
            <Box sx={{ p: 2, borderBottom: DS_BORDER, bgcolor: '#EEF2FF' }}>
              <Stack useFlexGap direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                <Stack useFlexGap direction="row" alignItems="center" spacing={1}>
                  <IconCurrencyDollar size={24} color="#4338CA" />
                  <Typography variant="h6" fontWeight={700} sx={{ fontFamily: DS_FONT, color: '#1E1B4B' }}>الشيكات غير المستلمة ({cheques.count})</Typography>
                </Stack>
                <Stack useFlexGap direction="row" spacing={1}>
                  <Button
                    className="no-print"
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={handleOpenTransferredChequesDialog}
                    sx={{
                      borderRadius: 2.5,
                      fontWeight: 700,
                      textTransform: 'none',
                      fontFamily: DS_FONT,
                    }}
                  >
                    الشيكات المرحّلة ({transferredChequesCount})
                  </Button>
                  {!isClosedYear && (
                    <Button
                      variant="contained"
                      color="warning"
                      size="small"
                      startIcon={<TrendingFlatIcon />}
                      onClick={() => setTransferAllChequesDialogOpen(true)}
                      sx={{
                        borderRadius: 2.5,
                        fontWeight: 700,
                        textTransform: 'none',
                        fontFamily: DS_FONT,
                        boxShadow: 'none',
                      }}
                    >
                      ترحيل جميع الشيكات للسنة القادمة ({cheques.count})
                    </Button>
                  )}
                </Stack>
              </Stack>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>اسم الطالب</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>المبلغ</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>رقم الشيك</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>تاريخ الشيك</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>تاريخ الدفع</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>ملاحظات</TableCell>
                    <TableCell className="no-print" align="center" sx={{ fontWeight: 'bold' }}>إجراءات</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(isPrinting ? cheques.items : cheques.items.slice(chequesPage * chequesRowsPerPage, chequesPage * chequesRowsPerPage + chequesRowsPerPage)).map((cheque) => (
                    <TableRow key={cheque.id} hover>
                      <TableCell>{cheque.student_name}</TableCell>
                      <TableCell align="right">{cheque.amount.toFixed(2)} ₪</TableCell>
                      <TableCell>{cheque.cheque_number || '-'}</TableCell>
                      <TableCell>
                        {cheque.cheque_date ? new Date(cheque.cheque_date).toLocaleDateString('en-GB') : '-'}
                      </TableCell>
                      <TableCell>
                        {cheque.payment_date ? new Date(cheque.payment_date).toLocaleDateString('en-GB') : '-'}
                      </TableCell>
                      <TableCell>{cheque.note || '-'}</TableCell>
                      <TableCell className="no-print" align="center">
                        {isClosedYear ? (
                          <Typography variant="caption" color="text.secondary">السنة مغلقة</Typography>
                        ) : (
                          <>
                            <IconButton
                              color="success"
                              onClick={() => handleChequeApprovalClick(cheque)}
                              size="small"
                              title="تأكيد التحصيل"
                            >
                              <CheckCircleIcon />
                            </IconButton>
                            <IconButton
                              color="warning"
                              onClick={() => handleTransferSingleCheque(cheque)}
                              size="small"
                              title="ترحيل للسنة القادمة"
                            >
                              <TrendingFlatIcon />
                            </IconButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              className="no-print"
              component="div"
              count={cheques.items.length}
              page={chequesPage}
              onPageChange={(e, newPage) => setChequesPage(newPage)}
              rowsPerPage={chequesRowsPerPage}
              onRowsPerPageChange={(e) => { setChequesRowsPerPage(parseInt(e.target.value, 10)); setChequesPage(0); }}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="صفوف لكل صفحة"
              sx={{ direction: 'ltr' }}
            />
          </Paper>
        )}

        {/* Pending Payments Table */}
        {payments?.count > 0 && (
          <Paper
            className={isClosedYear ? 'no-print' : ''}
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
            elevation={0}
            sx={{ mb: 3, borderRadius: 3, bgcolor: '#fff', boxShadow: DS_SHADOW, border: DS_BORDER, overflow: 'hidden' }}
          >
            <Box sx={{ p: 2, borderBottom: DS_BORDER, bgcolor: '#EEF2FF' }}>
              <Stack useFlexGap direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                <Stack useFlexGap direction="row" alignItems="center" spacing={1}>
                  <IconSteeringWheel size={24} color="#4338CA" />
                  <Typography variant="h6" fontWeight={700} sx={{ fontFamily: DS_FONT, color: '#1E1B4B' }}>المدفوعات المعلقة ({payments.count})</Typography>
                </Stack>
                <Stack useFlexGap direction="row" spacing={1}>
                  <Button
                    className="no-print"
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={handleOpenTransferredPaymentsDialog}
                    sx={{
                      borderRadius: 2.5,
                      fontWeight: 700,
                      textTransform: 'none',
                      fontFamily: DS_FONT,
                    }}
                  >
                    الدفعات المرحّلة ({transferredPaymentsCount})
                  </Button>
                  {!isClosedYear && (
                    <Button
                      variant="contained"
                      color="warning"
                      size="small"
                      startIcon={<TrendingFlatIcon />}
                      onClick={() => setTransferAllPaymentsDialogOpen(true)}
                      sx={{
                        borderRadius: 2.5,
                        fontWeight: 700,
                        textTransform: 'none',
                        fontFamily: DS_FONT,
                        boxShadow: 'none',
                      }}
                    >
                      ترحيل جميع شيكات المدفوعات للسنة القادمة ({payments.count})
                    </Button>
                  )}
                </Stack>
              </Stack>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>رقم الدفعة</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>المستلم</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>نوع المستلم</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>المبلغ</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>رقم الشيك</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>تاريخ الشيك</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>تاريخ الدفع</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>السبب</TableCell>
                    <TableCell className="no-print" align="center" sx={{ fontWeight: 'bold' }}>إجراءات</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(isPrinting ? payments.items : payments.items.slice(paymentsPage * paymentsRowsPerPage, paymentsPage * paymentsRowsPerPage + paymentsRowsPerPage)).map((payment) => (
                    <TableRow key={payment.id} hover>
                      <TableCell>#{payment.number || '-'}</TableCell>
                      <TableCell>{payment.recipient_name || '-'}</TableCell>
                      <TableCell>
                        {payment.recipient_type === 'employee' ? 'موظف' :
                         payment.recipient_type === 'bus' ? 'حافلة' :
                         payment.recipient_type === 'authorized' ? 'مخول' : 'أخرى'}
                      </TableCell>
                      <TableCell align="right">{payment.amount.toFixed(2)} ₪</TableCell>
                      <TableCell>{payment.cheque_number || '-'}</TableCell>
                      <TableCell>
                        {payment.cheque_date ? new Date(payment.cheque_date).toLocaleDateString('en-GB') : '-'}
                      </TableCell>
                      <TableCell>
                        {payment.payment_date ? new Date(payment.payment_date).toLocaleDateString('en-GB') : '-'}
                      </TableCell>
                      <TableCell>{payment.reason || '-'}</TableCell>
                      <TableCell className="no-print" align="center">
                        {isClosedYear ? (
                          <Typography variant="caption" color="text.secondary">السنة مغلقة</Typography>
                        ) : (
                          <IconButton
                            color="warning"
                            onClick={() => handleTransferSinglePayment(payment)}
                            size="small"
                            title="ترحيل للسنة القادمة"
                          >
                            <TrendingFlatIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              className="no-print"
              component="div"
              count={payments.items.length}
              page={paymentsPage}
              onPageChange={(e, newPage) => setPaymentsPage(newPage)}
              rowsPerPage={paymentsRowsPerPage}
              onRowsPerPageChange={(e) => { setPaymentsRowsPerPage(parseInt(e.target.value, 10)); setPaymentsPage(0); }}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="صفوف لكل صفحة"
              sx={{ direction: 'ltr' }}
            />
          </Paper>
        )}

        {/* Unfinalized Employee Salaries Table */}
        {employees?.count > 0 && (
          <Paper
            className={isClosedYear ? 'no-print' : ''}
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
            elevation={0}
            sx={{ mb: 3, borderRadius: 3, bgcolor: '#fff', boxShadow: DS_SHADOW, border: DS_BORDER, overflow: 'hidden' }}
          >
            <Box sx={{ p: 2, borderBottom: DS_BORDER, bgcolor: '#EEF2FF' }}>
              <Stack useFlexGap direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                <Stack useFlexGap direction="row" alignItems="center" spacing={1}>
                  <IconUserCheck size={24} color="#0284C7" />
                  <Typography variant="h6" fontWeight={700} sx={{ fontFamily: DS_FONT, color: '#1E1B4B' }}>رواتب موظفين غير مُنهاة ({employees.count})</Typography>
                </Stack>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate('/staff')}
                  sx={{
                    borderRadius: 2.5,
                    fontWeight: 700,
                    textTransform: 'none',
                    fontFamily: DS_FONT,
                    borderColor: '#0284C7',
                    color: '#0284C7',
                    '&:hover': { borderColor: '#0369A1', bgcolor: '#E0F2FE' },
                  }}
                >
                  مراجعة رواتب الموظفين
                </Button>
              </Stack>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>اسم الموظف</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>الشهر</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>الراتب الصافي</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>المدفوع</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>المتبقي</TableCell>
                    <TableCell className="no-print" align="center" sx={{ fontWeight: 'bold' }}>إجراءات</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(isPrinting ? employees.items : employees.items.slice(employeesPage * employeesRowsPerPage, employeesPage * employeesRowsPerPage + employeesRowsPerPage)).map((emp) => (
                    <TableRow key={emp.id} hover>
                      <TableCell>{emp.employee_name}</TableCell>
                      <TableCell>{emp.month}</TableCell>
                      <TableCell align="right">{emp.net_salary?.toFixed(2) || '0.00'} ₪</TableCell>
                      <TableCell align="right">{emp.total_paid?.toFixed(2) || '0.00'} ₪</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="error.main" fontWeight="bold">
                          {emp.outstanding_balance?.toFixed(2) || '0.00'} ₪
                        </Typography>
                      </TableCell>
                      <TableCell className="no-print" align="center">
                        {isClosedYear ? (
                          <Typography variant="caption" color="text.secondary">السنة مغلقة</Typography>
                        ) : (
                          <IconButton
                            color="primary"
                            onClick={() => navigate(`/staff/finance/${emp.employee_id}`)}
                            size="small"
                            title="إنهاء الراتب"
                          >
                            <PaymentIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              className="no-print"
              component="div"
              count={employees.items.length}
              page={employeesPage}
              onPageChange={(e, newPage) => setEmployeesPage(newPage)}
              rowsPerPage={employeesRowsPerPage}
              onRowsPerPageChange={(e) => { setEmployeesRowsPerPage(parseInt(e.target.value, 10)); setEmployeesPage(0); }}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="صفوف لكل صفحة"
              sx={{ direction: 'ltr' }}
            />
          </Paper>
        )}

      {/* Payment Dialog */}
      <Dialog 
        open={paymentDialogOpen} 
        onClose={() => setPaymentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>إكمال الدفع - {selectedStudentForPayment?.name}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="المبلغ"
                type="number"
                value={paymentData.amount}
                onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>نوع الدفع</InputLabel>
                <Select
                  value={paymentData.payment_type}
                  onChange={(e) => setPaymentData({ ...paymentData, payment_type: e.target.value })}
                  label="نوع الدفع"
                >
                  {paymentTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.display_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="تاريخ الدفع"
                type="date"
                value={paymentData.date}
                onChange={(e) => setPaymentData({ ...paymentData, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="ملاحظات"
                multiline
                rows={3}
                value={paymentData.note}
                onChange={(e) => setPaymentData({ ...paymentData, note: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={paymentData.received}
                    onChange={(e) => setPaymentData({ ...paymentData, received: e.target.checked })}
                  />
                }
                label="تم الاستلام"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialogOpen(false)}>إلغاء</Button>
          <Button 
            onClick={handlePaymentSave} 
            variant="contained" 
            disabled={isSavingPayment}
          >
            {isSavingPayment ? 'جاري الحفظ...' : 'حفظ'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Carry Debt Forward Dialog */}
      <Dialog
        open={carryDebtDialogOpen}
        onClose={() => setCarryDebtDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>ترحيل دين الطالب: {selectedStudentForCarry?.name}</DialogTitle>
        <DialogContent>
          {selectedStudentForCarry && (
            <Box>
              <Typography variant="body1" gutterBottom>
                <strong>إجمالي الرسوم:</strong> {selectedStudentForCarry.total_fee?.toFixed(2) || '0.00'} ₪
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>المدفوع:</strong> {selectedStudentForCarry.total_paid?.toFixed(2) || '0.00'} ₪
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>المتبقي:</strong> {selectedStudentForCarry.outstanding?.toFixed(2) || '0.00'} ₪
              </Typography>
              <TextField
                fullWidth
                label="المبلغ المرحّل كدين سابق للسنة القادمة"
                type="number"
                value={carryAmount}
                onChange={(e) => setCarryAmount(e.target.value)}
                helperText="يمكن تعديل المبلغ (مثلاً لإعفاء جزء من الدين)"
                inputProps={{ min: 0, max: selectedStudentForCarry.outstanding ?? undefined, step: '0.01' }}
                sx={{ mt: 2 }}
              />
              <Alert severity="warning" sx={{ mt: 2 }}>
                هذا الإجراء نهائي ولا يمكن التراجع عنه — سيتم إغلاق حساب الطالب لهذه السنة.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCarryDebtDialogOpen(false)}>إلغاء</Button>
          <Button
            onClick={handleCarryDebtConfirm}
            variant="contained"
            color="warning"
            disabled={isCarryingDebt || carryAmount === ''}
          >
            {isCarryingDebt ? 'جاري الترحيل...' : 'تأكيد الترحيل'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transferred (Closed) Students Dialog */}
      <Dialog
        className="no-print"
        open={transferredStudentsDialogOpen}
        onClose={() => setTransferredStudentsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>الطلاب المرحّلون ({transferredStudentsCount})</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: '#6B7280' }}>
            الطلاب الذين تم ترحيل دينهم وإغلاق حسابهم لهذه السنة. يمكنك التراجع عن الترحيل لإعادة أي طالب
            إلى قائمة "الطلاب الذين لم يكملوا دفع الرسوم المدرسية".
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="بحث بالاسم أو رقم الطالب..."
            value={transferredStudentsSearch}
            onChange={handleTransferredStudentsSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          {isLoadingTransferredStudents ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : transferredStudentsList.length > 0 ? (
            <List dense sx={{ maxHeight: 340, overflowY: 'auto' }}>
              {transferredStudentsList.map((student) => (
                <ListItem key={student.history_id} divider>
                  <ListItemText
                    primary={student.name}
                    secondary={`${student.class || '-'} — ${student.student_number || ''} — دين مرحّل: ${student.carry_forward_amount.toFixed(2)} ₪`}
                  />
                  <ListItemSecondaryAction>
                    <Stack direction="row" spacing={0.5}>
                      <IconButton
                        size="small"
                        color="default"
                        title="عرض تفاصيل الطالب"
                        onClick={() => setViewTransferredStudent(student)}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                      {school_year?.is_active ? (
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          startIcon={<UndoIcon />}
                          onClick={() => handleReopenStudent(student)}
                          disabled={reopeningStudentId === student.student_id}
                        >
                          {reopeningStudentId === student.student_id ? 'جاري الاستعادة...' : 'استعادة'}
                        </Button>
                      ) : (
                        <Typography variant="caption" color="text.secondary">السنة مغلقة</Typography>
                      )}
                    </Stack>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          ) : (
            <Alert severity="info">لا يوجد طلاب مرحّلون مطابقون لهذا البحث</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferredStudentsDialogOpen(false)}>إغلاق</Button>
        </DialogActions>
      </Dialog>

      {/* Transferred (Pending-Transfer) Cheques Dialog */}
      <Dialog
        className="no-print"
        open={transferredChequesDialogOpen}
        onClose={() => setTransferredChequesDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>الشيكات المرحّلة ({transferredChequesCount})</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: '#6B7280' }}>
            الشيكات التي تم ترحيلها للسنة القادمة (لم تُحصّل بعد). يمكنك التراجع عن ترحيل أي شيك لإعادته إلى
            قائمة "الشيكات غير المستلمة".
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="بحث باسم الطالب أو رقم الشيك..."
            value={transferredChequesSearch}
            onChange={handleTransferredChequesSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          {isLoadingTransferredCheques ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : transferredChequesList.length > 0 ? (
            <List dense sx={{ maxHeight: 340, overflowY: 'auto' }}>
              {transferredChequesList.map((cheque) => (
                <ListItem key={cheque.id} divider>
                  <ListItemText
                    primary={
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <span>{cheque.student_name}</span>
                        <Chip
                          size="small"
                          label={cheque.moved ? 'انتقلت للسنة الجديدة' : 'لا تزال في هذه السنة'}
                          color={cheque.moved ? 'default' : 'warning'}
                          variant="outlined"
                        />
                      </Stack>
                    }
                    secondary={`رقم الشيك: ${cheque.cheque_number || '-'} — ${cheque.amount.toFixed(2)} ₪`}
                  />
                  <ListItemSecondaryAction>
                    <Stack direction="row" spacing={0.5}>
                      <IconButton
                        size="small"
                        color="default"
                        title="عرض تفاصيل الشيك"
                        onClick={() => setViewTransferredCheque(cheque)}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                      {school_year?.is_active ? (
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          startIcon={<UndoIcon />}
                          onClick={() => handleUndoTransferCheque(cheque)}
                          disabled={restoringChequeId === cheque.id}
                        >
                          {restoringChequeId === cheque.id ? 'جاري الاستعادة...' : 'استعادة'}
                        </Button>
                      ) : (
                        <Typography variant="caption" color="text.secondary">السنة مغلقة</Typography>
                      )}
                    </Stack>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          ) : (
            <Alert severity="info">لا توجد شيكات مرحّلة مطابقة لهذا البحث</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferredChequesDialogOpen(false)}>إغلاق</Button>
        </DialogActions>
      </Dialog>

      {/* Transferred (Pending-Transfer) Payments Dialog */}
      <Dialog
        className="no-print"
        open={transferredPaymentsDialogOpen}
        onClose={() => setTransferredPaymentsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>الدفعات المرحّلة ({transferredPaymentsCount})</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: '#6B7280' }}>
            شيكات الدفعات الصادرة التي تم ترحيلها للسنة القادمة (لم تُصرف بعد). يمكنك التراجع عن ترحيل أي شيك
            لإعادته إلى قائمة "المدفوعات المعلقة".
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="بحث بالمستلم أو رقم الشيك أو السبب..."
            value={transferredPaymentsSearch}
            onChange={handleTransferredPaymentsSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          {isLoadingTransferredPayments ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : transferredPaymentsList.length > 0 ? (
            <List dense sx={{ maxHeight: 340, overflowY: 'auto' }}>
              {transferredPaymentsList.map((payment) => (
                <ListItem key={payment.id} divider>
                  <ListItemText
                    primary={
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <span>{payment.recipient_name || '-'}</span>
                        <Chip
                          size="small"
                          label={payment.moved ? 'انتقلت للسنة الجديدة' : 'لا تزال في هذه السنة'}
                          color={payment.moved ? 'default' : 'warning'}
                          variant="outlined"
                        />
                      </Stack>
                    }
                    secondary={`رقم الشيك: ${payment.cheque_number || '-'} — ${payment.amount.toFixed(2)} ₪`}
                  />
                  <ListItemSecondaryAction>
                    <Stack direction="row" spacing={0.5}>
                      <IconButton
                        size="small"
                        color="default"
                        title="عرض تفاصيل الدفعة"
                        onClick={() => setViewTransferredPayment(payment)}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                      {school_year?.is_active ? (
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          startIcon={<UndoIcon />}
                          onClick={() => handleUndoTransferPayment(payment)}
                          disabled={restoringPaymentId === payment.id}
                        >
                          {restoringPaymentId === payment.id ? 'جاري الاستعادة...' : 'استعادة'}
                        </Button>
                      ) : (
                        <Typography variant="caption" color="text.secondary">السنة مغلقة</Typography>
                      )}
                    </Stack>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          ) : (
            <Alert severity="info">لا توجد دفعات مرحّلة مطابقة لهذا البحث</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferredPaymentsDialogOpen(false)}>إغلاق</Button>
        </DialogActions>
      </Dialog>

      {/* Transferred Student Detail Dialog */}
      <Dialog
        className="no-print"
        open={!!viewTransferredStudent}
        onClose={() => setViewTransferredStudent(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>تفاصيل الطالب المرحّل</DialogTitle>
        <DialogContent>
          {viewTransferredStudent && (
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <Typography variant="body1"><strong>الاسم:</strong> {viewTransferredStudent.name}</Typography>
              <Typography variant="body1"><strong>رقم الطالب:</strong> {viewTransferredStudent.student_number || '-'}</Typography>
              <Typography variant="body1"><strong>الصف:</strong> {viewTransferredStudent.class || '-'}</Typography>
              <Typography variant="body1"><strong>المدفوع:</strong> {viewTransferredStudent.total_paid.toFixed(2)} ₪</Typography>
              <Typography variant="body1"><strong>الدين المرحّل:</strong> {viewTransferredStudent.carry_forward_amount.toFixed(2)} ₪</Typography>
              <Typography variant="body1">
                <strong>تاريخ الإغلاق:</strong> {viewTransferredStudent.closed_at ? new Date(viewTransferredStudent.closed_at).toLocaleDateString('en-GB') : '-'}
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewTransferredStudent(null)}>إغلاق</Button>
        </DialogActions>
      </Dialog>

      {/* Transferred Cheque Detail Dialog */}
      <Dialog
        className="no-print"
        open={!!viewTransferredCheque}
        onClose={() => setViewTransferredCheque(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>تفاصيل الشيك المرحّل</DialogTitle>
        <DialogContent>
          {viewTransferredCheque && (
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <Typography variant="body1"><strong>اسم الطالب:</strong> {viewTransferredCheque.student_name}</Typography>
              <Typography variant="body1"><strong>المبلغ:</strong> {viewTransferredCheque.amount.toFixed(2)} ₪</Typography>
              <Typography variant="body1"><strong>رقم الشيك:</strong> {viewTransferredCheque.cheque_number || '-'}</Typography>
              <Typography variant="body1">
                <strong>تاريخ الدفع:</strong> {viewTransferredCheque.payment_date ? new Date(viewTransferredCheque.payment_date).toLocaleDateString('en-GB') : '-'}
              </Typography>
              <Typography variant="body1"><strong>ملاحظات:</strong> {viewTransferredCheque.note || '-'}</Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewTransferredCheque(null)}>إغلاق</Button>
        </DialogActions>
      </Dialog>

      {/* Transferred Payment Detail Dialog */}
      <Dialog
        className="no-print"
        open={!!viewTransferredPayment}
        onClose={() => setViewTransferredPayment(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>تفاصيل الدفعة المرحّلة</DialogTitle>
        <DialogContent>
          {viewTransferredPayment && (
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <Typography variant="body1"><strong>رقم الدفعة:</strong> #{viewTransferredPayment.number || '-'}</Typography>
              <Typography variant="body1"><strong>المستلم:</strong> {viewTransferredPayment.recipient_name || '-'}</Typography>
              <Typography variant="body1"><strong>المبلغ:</strong> {viewTransferredPayment.amount.toFixed(2)} ₪</Typography>
              <Typography variant="body1"><strong>رقم الشيك:</strong> {viewTransferredPayment.cheque_number || '-'}</Typography>
              <Typography variant="body1">
                <strong>تاريخ الدفع:</strong> {viewTransferredPayment.payment_date ? new Date(viewTransferredPayment.payment_date).toLocaleDateString('en-GB') : '-'}
              </Typography>
              <Typography variant="body1"><strong>السبب:</strong> {viewTransferredPayment.reason || '-'}</Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewTransferredPayment(null)}>إغلاق</Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Carry-All-Debt Confirmation Dialog */}
      <Dialog
        open={carryAllDialogOpen}
        onClose={() => setCarryAllDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>ترحيل دين جميع الطلاب ({students?.count || 0})</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            سيتم إغلاق حساب كل طالب من الطلاب التالي أسماؤهم لهذه السنة، وترحيل كامل المبلغ المتبقي عليه كدين سابق للسنة القادمة:
          </Typography>
          <List dense sx={{ maxHeight: 260, overflowY: 'auto', mt: 1 }}>
            {(students?.items || []).map((student) => (
              <ListItem key={student.id} divider>
                <ListItemText
                  primary={student.name}
                  secondary={`${student.class || '-'} — ${student.student_id || ''}`}
                />
                <ListItemSecondaryAction>
                  <Typography variant="body2" color="error.main" fontWeight="bold">
                    {student.outstanding?.toFixed(2) || '0.00'} ₪
                  </Typography>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 2 }}>
            الإجمالي: {(students?.total_outstanding || 0).toFixed(2)} ₪
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            هذا الإجراء نهائي ولا يمكن التراجع عنه — سيتم إغلاق حسابات جميع الطلاب أعلاه لهذه السنة.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCarryAllDialogOpen(false)}>إلغاء</Button>
          <Button
            onClick={handleCarryAllDebtConfirm}
            variant="contained"
            color="warning"
            disabled={isCarryingAllDebt}
          >
            {isCarryingAllDebt ? 'جاري الترحيل...' : 'تأكيد ترحيل الكل'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pass to Next Year — Summary & Confirm Dialog */}
      <Dialog
        className="no-print"
        open={passToNextYearDialogOpen}
        onClose={() => setPassToNextYearDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>مراجعة وتأكيد الانتقال إلى السنة الدراسية الجديدة</DialogTitle>
        <DialogContent>
          <Stack useFlexGap direction="row" alignItems="center" justifyContent="center" spacing={2} sx={{ mb: 2, p: 2, borderRadius: 2, bgcolor: '#F8F9FF' }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">السنة الحالية</Typography>
              <Typography variant="h6" fontWeight={800}>{school_year?.label || '-'}</Typography>
            </Box>
            <TrendingFlatIcon sx={{ color: '#4338CA', transform: 'scaleX(-1)' }} />
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">السنة الجديدة</Typography>
              <Typography variant="h6" fontWeight={800} color="#16A34A">{transitionData?.new_year?.label || '-'}</Typography>
            </Box>
          </Stack>

          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>ملخص الحالة</Typography>
          <Stack useFlexGap direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
            <Chip size="small" label={`طلاب مفتوح: ${students?.count || 0}`} color={students?.count > 0 ? 'error' : 'success'} />
            <Chip size="small" label={`طلاب مرحّل: ${transferredStudentsCount}`} color={transferredStudentsCount > 0 ? 'warning' : 'default'} />
            <Chip size="small" label={`شيكات مفتوحة: ${cheques?.count || 0}`} color={cheques?.count > 0 ? 'error' : 'success'} />
            <Chip size="small" label={`شيكات مرحّلة: ${transferredChequesCount}`} color={transferredChequesCount > 0 ? 'warning' : 'default'} />
            <Chip size="small" label={`دفعات مفتوحة: ${payments?.count || 0}`} color={payments?.count > 0 ? 'error' : 'success'} />
            <Chip size="small" label={`دفعات مرحّلة: ${transferredPaymentsCount}`} color={transferredPaymentsCount > 0 ? 'warning' : 'default'} />
            <Chip size="small" label={`رواتب غير مُنهاة: ${employees?.count || 0}`} color={employees?.count > 0 ? 'error' : 'success'} />
          </Stack>

          {summary?.total_open_items > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              لا يزال هناك {summary.total_open_items} عنصر مفتوح لم يُحل ولم يُرحّل. سيتم إتمام الانتقال بدون
              لمسها — ستبقى مرتبطة بالسنة الحالية (التي ستصبح غير نشطة)، ويمكنك متابعتها لاحقاً باختيار هذه
              السنة من القائمة.
            </Alert>
          )}

          <Accordion disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={700}>العناصر التي لا تزال مفتوحة ({(students?.count || 0) + (cheques?.count || 0) + (payments?.count || 0)})</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ maxHeight: 260, overflowY: 'auto' }}>
              {(students?.count || 0) + (cheques?.count || 0) + (payments?.count || 0) === 0 ? (
                <Typography variant="body2" color="text.secondary">لا يوجد عناصر مفتوحة</Typography>
              ) : (
                <List dense>
                  {(students?.items || []).map((s) => (
                    <ListItem key={`os-${s.id}`} divider>
                      <ListItemText primary={`طالب: ${s.name}`} secondary={`متبقي: ${s.outstanding?.toFixed(2)} ₪`} />
                    </ListItem>
                  ))}
                  {(cheques?.items || []).map((c) => (
                    <ListItem key={`oc-${c.id}`} divider>
                      <ListItemText primary={`شيك: ${c.student_name}`} secondary={`${c.amount?.toFixed(2)} ₪ — رقم ${c.cheque_number || '-'}`} />
                    </ListItem>
                  ))}
                  {(payments?.items || []).map((p) => (
                    <ListItem key={`op-${p.id}`} divider>
                      <ListItemText primary={`دفعة: ${p.recipient_name || '-'}`} secondary={`${p.amount?.toFixed(2)} ₪ — رقم الشيك ${p.cheque_number || '-'}`} />
                    </ListItem>
                  ))}
                </List>
              )}
            </AccordionDetails>
          </Accordion>

          <Accordion disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={700}>العناصر التي تم ترحيلها ({transferredStudentsCount + transferredChequesCount + transferredPaymentsCount})</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ maxHeight: 260, overflowY: 'auto' }}>
              {transferredStudentsCount + transferredChequesCount + transferredPaymentsCount === 0 ? (
                <Typography variant="body2" color="text.secondary">لا يوجد عناصر مرحّلة</Typography>
              ) : (
                <List dense>
                  {transferredStudentsList.map((s) => (
                    <ListItem key={`ts-${s.history_id}`} divider>
                      <ListItemText primary={`طالب: ${s.name}`} secondary={`دين مرحّل: ${s.carry_forward_amount.toFixed(2)} ₪`} />
                    </ListItem>
                  ))}
                  {transferredChequesList.map((c) => (
                    <ListItem key={`tc-${c.id}`} divider>
                      <ListItemText primary={`شيك: ${c.student_name}`} secondary={`${c.amount?.toFixed(2)} ₪ — رقم ${c.cheque_number || '-'}`} />
                    </ListItem>
                  ))}
                  {transferredPaymentsList.map((p) => (
                    <ListItem key={`tp-${p.id}`} divider>
                      <ListItemText primary={`دفعة: ${p.recipient_name || '-'}`} secondary={`${p.amount?.toFixed(2)} ₪ — رقم الشيك ${p.cheque_number || '-'}`} />
                    </ListItem>
                  ))}
                </List>
              )}
            </AccordionDetails>
          </Accordion>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPassToNextYearDialogOpen(false)}>إلغاء</Button>
          <Button
            onClick={handleCompleteTransition}
            variant="contained"
            color={summary?.total_open_items > 0 ? 'warning' : 'success'}
            disabled={isCompletingTransition}
          >
            {isCompletingTransition ? 'جاري الإتمام...' : 'تأكيد الانتقال للسنة الجديدة'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Transfer-All-Cheques Dialog */}
      <Dialog
        open={transferAllChequesDialogOpen}
        onClose={() => setTransferAllChequesDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>ترحيل جميع الشيكات للسنة القادمة ({cheques?.count || 0})</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            لم يتم تحصيل هذه الشيكات بعد. هذا الإجراء لا يسجّلها كمحصّلة — فقط يرحّلها إلى دفاتر السنة الدراسية القادمة لمتابعة تحصيلها هناك، مع إخراجها من قائمة عناصر هذه السنة المفتوحة.
          </Alert>
          <List dense sx={{ maxHeight: 260, overflowY: 'auto', mt: 1 }}>
            {(cheques?.items || []).map((cheque) => (
              <ListItem key={cheque.id} divider>
                <ListItemText
                  primary={cheque.student_name}
                  secondary={`رقم الشيك: ${cheque.cheque_number || '-'}`}
                />
                <ListItemSecondaryAction>
                  <Typography variant="body2" fontWeight="bold">
                    {cheque.amount?.toFixed(2) || '0.00'} ₪
                  </Typography>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 2 }}>
            الإجمالي: {(cheques?.total_amount || 0).toFixed(2)} ₪
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferAllChequesDialogOpen(false)}>إلغاء</Button>
          <Button
            onClick={handleTransferAllChequesConfirm}
            variant="contained"
            color="warning"
            disabled={isTransferringAllCheques}
          >
            {isTransferringAllCheques ? 'جاري الترحيل...' : 'ترحيل الكل'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Transfer-All-Pending-Payments Dialog */}
      <Dialog
        open={transferAllPaymentsDialogOpen}
        onClose={() => setTransferAllPaymentsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>ترحيل جميع شيكات المدفوعات المعلقة للسنة القادمة ({payments?.count || 0})</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            لم يتم صرف هذه الشيكات بعد. هذا الإجراء لا يسجّلها كمصروفة — فقط يرحّلها إلى دفاتر السنة الدراسية القادمة لمتابعة صرفها هناك، مع إخراجها من قائمة عناصر هذه السنة المفتوحة.
          </Alert>
          <List dense sx={{ maxHeight: 260, overflowY: 'auto', mt: 1 }}>
            {(payments?.items || []).map((payment) => (
              <ListItem key={payment.id} divider>
                <ListItemText
                  primary={payment.recipient_name || '-'}
                  secondary={`رقم الشيك: ${payment.cheque_number || '-'}`}
                />
                <ListItemSecondaryAction>
                  <Typography variant="body2" fontWeight="bold">
                    {payment.amount?.toFixed(2) || '0.00'} ₪
                  </Typography>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 2 }}>
            الإجمالي: {(payments?.total_amount || 0).toFixed(2)} ₪
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferAllPaymentsDialogOpen(false)}>إلغاء</Button>
          <Button
            onClick={handleTransferAllPaymentsConfirm}
            variant="contained"
            color="warning"
            disabled={isTransferringAllPayments}
          >
            {isTransferringAllPayments ? 'جاري الترحيل...' : 'ترحيل الكل'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cheque Approval Confirmation Dialog */}
      <Dialog 
        open={chequeApprovalDialog.open} 
        onClose={() => setChequeApprovalDialog({ open: false, cheque: null })}
      >
        <DialogTitle>تأكيد تحصيل الشيك</DialogTitle>
        <DialogContent>
          {chequeApprovalDialog.cheque && (
            <Box>
              <Typography variant="body1" gutterBottom>
                <strong>اسم الطالب:</strong> {chequeApprovalDialog.cheque.student_name}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>المبلغ:</strong> {chequeApprovalDialog.cheque.amount.toFixed(2)} ₪
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>رقم الشيك:</strong> {chequeApprovalDialog.cheque.cheque_number || '-'}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>تاريخ الشيك:</strong> {
                  chequeApprovalDialog.cheque.cheque_date 
                    ? new Date(chequeApprovalDialog.cheque.cheque_date).toLocaleDateString('en-GB')
                    : '-'
                }
              </Typography>
              <Alert severity="warning" sx={{ mt: 2 }}>
                هل أنت متأكد من تأكيد تحصيل هذا الشيك؟
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChequeApprovalDialog({ open: false, cheque: null })}>
            إلغاء
          </Button>
          <Button 
            onClick={handleChequeApprovalConfirm} 
            variant="contained" 
            color="success"
          >
            تأكيد التحصيل
          </Button>
        </DialogActions>
      </Dialog>

      {/* Empty State */}
      {!isClosedYear && summary?.total_open_items === 0 && (
        (transferredStudentsCount + transferredChequesCount + transferredPaymentsCount) === 0 ? (
          <Alert severity="success" sx={{ mt: 3, borderRadius: 2, fontFamily: DS_FONT }}>
            <Typography variant="h6" gutterBottom fontWeight={800} sx={{ fontFamily: DS_FONT }}>✅ لا توجد عناصر مفتوحة</Typography>
            <Typography variant="body2" sx={{ fontFamily: DS_FONT }}>يمكنك الآن إغلاق السنة الدراسية بأمان</Typography>
          </Alert>
        ) : (
          <Alert severity="warning" sx={{ mt: 3, borderRadius: 2, fontFamily: DS_FONT }}>
            <Typography variant="h6" gutterBottom fontWeight={800} sx={{ fontFamily: DS_FONT }}>
              ⚠️ لا توجد عناصر مفتوحة حالياً، لكن يوجد {transferredStudentsCount + transferredChequesCount + transferredPaymentsCount} عنصر تم ترحيله للسنة القادمة
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: DS_FONT, mb: 1.5 }}>
              {transferredStudentsCount > 0 && `${transferredStudentsCount} طالب`}
              {transferredStudentsCount > 0 && (transferredChequesCount > 0 || transferredPaymentsCount > 0) && '، '}
              {transferredChequesCount > 0 && `${transferredChequesCount} شيك`}
              {transferredChequesCount > 0 && transferredPaymentsCount > 0 && '، '}
              {transferredPaymentsCount > 0 && `${transferredPaymentsCount} دفعة معلقة`}
              {' '}لم تُحل فعلياً — تم تأجيلها فقط. اضغط على الأزرار أدناه لمراجعتها أو التراجع عن الترحيل.
            </Typography>
            <Stack useFlexGap direction="row" spacing={1} flexWrap="wrap" gap={1}>
              {transferredStudentsCount > 0 && (
                <Button
                  variant="contained"
                  size="small"
                  color="warning"
                  onClick={handleOpenTransferredStudentsDialog}
                  sx={{ borderRadius: 2.5, fontWeight: 700, textTransform: 'none', fontFamily: DS_FONT }}
                >
                  عرض الطلاب المرحّلين ({transferredStudentsCount})
                </Button>
              )}
              {transferredChequesCount > 0 && (
                <Button
                  variant="contained"
                  size="small"
                  color="warning"
                  onClick={handleOpenTransferredChequesDialog}
                  sx={{ borderRadius: 2.5, fontWeight: 700, textTransform: 'none', fontFamily: DS_FONT }}
                >
                  عرض الشيكات المرحّلة ({transferredChequesCount})
                </Button>
              )}
              {transferredPaymentsCount > 0 && (
                <Button
                  variant="contained"
                  size="small"
                  color="warning"
                  onClick={handleOpenTransferredPaymentsDialog}
                  sx={{ borderRadius: 2.5, fontWeight: 700, textTransform: 'none', fontFamily: DS_FONT }}
                >
                  عرض الدفعات المرحّلة ({transferredPaymentsCount})
                </Button>
              )}
            </Stack>
          </Alert>
        )
      )}

      {/* Action Buttons */}
      {summary?.total_open_items > 0 && (
        <Paper className="no-print" elevation={0} sx={{ p: 3, mt: 3, borderRadius: 3, bgcolor: '#fff', boxShadow: DS_SHADOW, border: DS_BORDER }}>
          <Typography variant="h6" gutterBottom fontWeight={700} sx={{ fontFamily: DS_FONT, color: '#1E1B4B' }}>إجراءات مطلوبة</Typography>
          <Stack direction="row" spacing={2} mt={2} flexWrap="wrap" useFlexGap>
            {students?.count > 0 && (
              <Button
                variant="outlined"
                startIcon={<IconUsers />}
                onClick={() => navigate('/students')}
                sx={{
                  borderRadius: 2.5,
                  fontWeight: 700,
                  textTransform: 'none',
                  fontFamily: DS_FONT,
                  borderColor: '#4338CA',
                  color: '#4338CA',
                  '&:hover': { borderColor: '#3730A3', bgcolor: '#EEF2FF' },
                }}
              >
                مراجعة حسابات الطلاب ({students.count})
              </Button>
            )}
            {cheques?.count > 0 && (
              <Button
                variant="outlined"
                startIcon={<IconCurrencyDollar />}
                onClick={() => navigate('/cheques')}
                sx={{
                  borderRadius: 2.5,
                  fontWeight: 700,
                  textTransform: 'none',
                  fontFamily: DS_FONT,
                  borderColor: '#4338CA',
                  color: '#4338CA',
                  '&:hover': { borderColor: '#3730A3', bgcolor: '#EEF2FF' },
                }}
              >
                مراجعة الشيكات ({cheques.count})
              </Button>
            )}
            {payments?.count > 0 && (
              <Button
                variant="outlined"
                startIcon={<IconSteeringWheel />}
                onClick={() => navigate('/finance')}
                sx={{
                  borderRadius: 2.5,
                  fontWeight: 700,
                  textTransform: 'none',
                  fontFamily: DS_FONT,
                  borderColor: '#4338CA',
                  color: '#4338CA',
                  '&:hover': { borderColor: '#3730A3', bgcolor: '#EEF2FF' },
                }}
              >
                مراجعة المدفوعات ({payments.count})
              </Button>
            )}
            {employees?.count > 0 && (
              <Button
                variant="outlined"
                startIcon={<IconUserCheck />}
                onClick={() => navigate('/staff')}
                sx={{
                  borderRadius: 2.5,
                  fontWeight: 700,
                  textTransform: 'none',
                  fontFamily: DS_FONT,
                  borderColor: '#4338CA',
                  color: '#4338CA',
                  '&:hover': { borderColor: '#3730A3', bgcolor: '#EEF2FF' },
                }}
              >
                مراجعة رواتب الموظفين ({employees.count})
              </Button>
            )}
          </Stack>
        </Paper>
      )}
        </>
      )}
      </Box>
    </Box>
  );
};

export default YearTransitionPage;
